import React from 'react';
import PropTypes from 'prop-types';

import ModalContent from './modal-content.js';

class Modal extends React.Component {
  constructor(props) {
    super(props);
    this.close = this.close.bind(this);
    this.hash = Math.random().toString();
    this.state = {
      isOpen: props.isOpen || false,
      isDragged: false,
      x: null,
      y: null,
      deltaX: 0,
      deltaY: 0,
      mouseOffsetX: 0,
      mouseOffsetY: 0
    };
  }

  componentDidMount() {
    if (this.props.isOpen) {
      this.setModalContext();
    }
  }

  componentWillReceiveProps(nextProps) {
    if (typeof nextProps.isOpen === 'boolean' && this.props.isOpen !== nextProps.isOpen) {
      if (nextProps.isOpen) {
        this.open();
      } else {
        this.close();
      }
    }
  }

  componentWillUnmount() {
    this.unsetModalContext();
  }

  setModalContext = () => {
    document.addEventListener('keydown', this.handleEsc);
    document.activeElement.blur();
    document.body.classList.add('body-modal-fix');
  }

  handleEsc = (event) => {
    const allModals = document.querySelectorAll('.pure-modal');
    if (allModals.length && !allModals[allModals.length - 1].classList.contains(this.hash))
      return false;
    if (typeof document.activeElement.value === 'undefined' && event.keyCode === 27) {
      this.close(event);
    }
  }

  unsetModalContext() {
    document.removeEventListener('keydown', this.handleEsc);
    document.body.classList.remove('body-modal-fix');
    this.setState({
      x: null,
      y: null,
      deltaX: 0,
      deltaY: 0,
      mouseOffsetX: 0,
      mouseOffsetY: 0
    });
  }

  getCoords = (e) => {
    let { pageX, pageY } = e;
    if (e.changedTouches && e.changedTouches.length === 1) {
      pageX = e.changedTouches[0].pageX;
      pageY = e.changedTouches[0].pageY;
    }
    return {
      pageX,
      pageY
    };
  }

  handleStartDrag = (e) => {
    if (e.changedTouches && e.changedTouches.length > 1) return false;

    e.preventDefault();

    const { pageX, pageY } = this.getCoords(e);
    const { top, left } = e.currentTarget.getBoundingClientRect();

    if (!this.state.isDragged) {
      window.addEventListener('click', this.handleEndDrag);
    }

    return this.setState({
      isDragged: true,
      x: typeof this.state.x === 'number' ? this.state.x : left,
      y: typeof this.state.y === 'number' ? this.state.y : top,
      mouseOffsetX: pageX - left,
      mouseOffsetY: pageY - top
    });
  }

  handleDrag = (e) => {
    if (e.changedTouches && e.changedTouches.lenght > 1) {
      return this.handleEndDrag();
    }

    e.preventDefault();

    const { pageX, pageY } = this.getCoords(e);

    return this.setState({
      deltaX: pageX - this.state.x - this.state.mouseOffsetX,
      deltaY: pageY - this.state.y - this.state.mouseOffsetY
    });
  }

  handleEndDrag = () => {
    window.removeEventListener('click', this.handleEndDrag);
    return this.setState({
      isDragged: false,
      x: null,
      y: null,
      mouseOffsetX: 0,
      mouseOffsetY: 0
    });
  }

  open = (event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (!this.state.isOpen) {
      this.setState({
        isOpen: true
      });
      this.setModalContext();
    }
  }

  close(event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (this.state.isOpen) {
      let isOpen = false;
      if (this.props.onClose && event) {
        isOpen = !this.props.onClose();
      }

      if (this.state.isOpen !== isOpen) {
        this.setState({
          isOpen
        });
        this.unsetModalContext();
      }
    }
  }

  handleBackdropClick = (event) => {
    const {cancelBackdropClick} = this.props;
    if (event) {
      if (cancelBackdropClick || !event.target.classList.contains('pure-modal-backdrop')) {
        return;
      }
      event.stopPropagation();
      event.preventDefault();
    }
    this.close(event);
  }

  render() {
    if (!this.state.isOpen) {
      return null;
    }

    const {
      children,
      replace,
      className,
      header,
      footer,
      scrollable,
      draggable,
      width
    } = this.props;

    let backdropClasses = ['pure-modal-backdrop'];
    let modalClasses = ['pure-modal', this.hash];
    let bodyClasses = ['panel-body'];

    if (className) {
      modalClasses.push(className);
    }

    if (scrollable) {
      bodyClasses.push('scrollable');
    } else {
      backdropClasses.push('scrollable');
      modalClasses.push('auto-height');
    }

    if (draggable) {
      backdropClasses.push('backdrop-overflow-hidden');
    }

    const handleDrag = this.state.isDragged ? this.handleDrag : null;

    return (
      <div
        className={backdropClasses.join(' ')}
        onMouseDown={this.handleBackdropClick}
        onTouchMove={handleDrag}
        onMouseMove={handleDrag}
      >
        <div
          className={modalClasses.join(' ')}
          style={{
            transform: `translate(${this.state.deltaX}px, ${this.state.deltaY}px)`,
            transition: 'none',
            width
          }}
        >
          <ModalContent
            replace={replace}
            header={header}
            footer={footer}
            onDragStart={draggable ? this.handleStartDrag : null}
            onDragEnd={draggable ? this.handleEndDrag : null}
            onClose={this.close}
            bodyClass={bodyClasses.join(' ')}
          >
            {children}
          </ModalContent>
        </div>
      </div>
    );
  }
}

Modal.defaultProps = {
  mode: 'modal',
  replace: false,
  scrollable: true,
  draggable: false
};

Modal.propTypes = {
  mode: PropTypes.oneOf(['modal', 'tooltip']),
  replace: PropTypes.bool,
  children: PropTypes.node,
  cancelBackdropClick: PropTypes.bool,
  isOpen: PropTypes.bool,
  scrollable: PropTypes.bool,
  draggable: PropTypes.bool,
  onClose: PropTypes.func,
  className: PropTypes.string,
  width: PropTypes.string,
  header: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  footer: PropTypes.oneOfType([PropTypes.node, PropTypes.string])
};

export default Modal;
